import React, { useMemo, useEffect, useLayoutEffect, useRef, useState } from 'react';
import moment from 'moment';
import MonthView from './MonthView';
import DayTimeSlots from './DayTimeSlots';
import './styles.css';
import { isMobile } from '../../../../../utils/mobile';

import { ReactComponent as ScheduleInfoSvg } from '../../../../../assets/img/schedule-info.svg';

export default function DoctorCalendar({
  currentDoctorSchedule,
  byDaysSchedule,
  selectedJobId,
  onOpenAppointment,
  isDirect,
  selectedDate,
  setSelectedDate,
  patients,
  jobs,
  isSelectedDateLoading,
  isModal = false,
}) {
  // State for mobile month navigation
  const [currentMobileMonthIndex, setCurrentMobileMonthIndex] = useState(0);
  const [modalMaxHeight, setModalMaxHeight] = useState(0);
  const daySlotsContentRef = useRef(null);
  
  // Prepare schedule data for calendar with specialization filtering
  const availableDays = useMemo(() => {
    const days = {};

    if (byDaysSchedule?.schedule) {
      byDaysSchedule.schedule.forEach((slot) => {
        if (selectedJobId && slot.job_id !== Number(selectedJobId)) {
          return;
        }

        const date = moment(slot.time_from || slot.datetime).format('YYYY-MM-DD');
        if (!days[date]) {
          days[date] = [];
        }
        days[date].push(slot);
      });
    }

    return days;
  }, [byDaysSchedule, selectedJobId]);

  const slotsByDate = useMemo(() => {
    const days = {};

    if (currentDoctorSchedule?.schedule) {
      currentDoctorSchedule.schedule.forEach((slot) => {
        
        // Фільтрація за спеціалізацією, якщо вона обрана
        if (selectedJobId && 
            slot.job_id !== Number(selectedJobId)) {
          return;
        }
        
        const date = moment(slot.datetime).format('YYYY-MM-DD');
        if (!days[date]) {
          days[date] = [];
        }
        days[date].push(slot);
      });
    }

    return days;
  }, [currentDoctorSchedule, selectedJobId]);
  
  // Calculate current month and next month
  const currentMonth = useMemo(() => moment().startOf('month'), []);
  const nextMonth = useMemo(() => moment().add(1, 'month').startOf('month'), []);
  
  // Available months for mobile navigation
  const availableMonths = useMemo(() => [currentMonth, nextMonth], [currentMonth, nextMonth]);
  
  // Mobile navigation functions
  const goToPreviousMonth = () => {
    if (currentMobileMonthIndex > 0) {
      setCurrentMobileMonthIndex(currentMobileMonthIndex - 1);
    }
  };
  
  const goToNextMonth = () => {
    if (currentMobileMonthIndex < availableMonths.length - 1) {
      setCurrentMobileMonthIndex(currentMobileMonthIndex + 1);
    }
  };
  
  // Set default selected date to today if available, or first available day
  useEffect(() => {
    // Якщо день не обраний, спробуємо обрати сьогоднішній або перший доступний
    if (!selectedDate && Object.keys(availableDays).length > 0) {
      const today = moment().format('YYYY-MM-DD');
      setSelectedDate(today);
      
      if (!availableDays[today]) {
        // Інакше вибираємо перший доступний день
        const firstAvailableDate = Object.keys(availableDays).sort()[0];
        if (firstAvailableDate) {
          setSelectedDate(firstAvailableDate);
        }
      }
    }
  }, [availableDays, selectedDate, setSelectedDate]);

  // Set correct mobile month index when selectedDate changes
  useEffect(() => {
    if (isMobile() && selectedDate) {
      const selectedMoment = moment(selectedDate);
      availableMonths.forEach((month, index) => {
        if (selectedMoment.format('YYYY-MM') === month.format('YYYY-MM')) {
          setCurrentMobileMonthIndex(index);
        }
      });
    }
  }, [selectedDate, availableMonths]);

  // Handle day selection
  const handleDaySelect = (date) => {
    setSelectedDate(date);
  };

  useLayoutEffect(() => {
    if (!isModal) {
      return;
    }

    const updateMaxHeight = () => {
      const measuredHeight = daySlotsContentRef.current?.offsetHeight || 0;
      if (measuredHeight > modalMaxHeight) {
        setModalMaxHeight(measuredHeight);
      }
    };

    updateMaxHeight();

    const observedNode = daySlotsContentRef.current;
    if (!observedNode || typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver(() => {
      updateMaxHeight();
    });
    observer.observe(observedNode);

    return () => {
      observer.disconnect();
    };
  }, [isModal, selectedDate, isSelectedDateLoading, currentDoctorSchedule, modalMaxHeight]);

  return (
    <div className="doctor-calendar-container">
      {isMobile() ? (
        <div className="doctor-calendar-months mobile-months">
          <MonthView 
            month={availableMonths[currentMobileMonthIndex]} 
            availableDays={availableDays} 
            selectedDate={selectedDate}
            onSelectDay={handleDaySelect}
            showNavigation={true}
            onPrevMonth={goToPreviousMonth}
            onNextMonth={goToNextMonth}
            canGoPrev={currentMobileMonthIndex > 0}
            canGoNext={currentMobileMonthIndex < availableMonths.length - 1}
          />
        </div>
      ) : (
        <div className="doctor-calendar-months">
          <MonthView 
            month={currentMonth} 
            availableDays={availableDays} 
            selectedDate={selectedDate}
            onSelectDay={handleDaySelect} 
          />
          <MonthView 
            month={nextMonth} 
            availableDays={availableDays} 
            selectedDate={selectedDate}
            onSelectDay={handleDaySelect} 
          />
        </div>
      )}
      
      {selectedDate && (
        <>
          <div
            className={`day-slots-host ${isModal ? 'day-slots-host--modal' : ''}`}
            style={isModal && modalMaxHeight > 0 ? { minHeight: `${modalMaxHeight}px` } : undefined}
          >
            <div ref={daySlotsContentRef}>
              {isSelectedDateLoading ? (
                <div className="day-time-slots schedule schedule-loading">
                  <div className="day-date">
                    <div className="day-date-text">Завантаження розкладу...</div>
                  </div>
                  <div className="schedule-loader-grid" aria-hidden="true">
                    <div className="schedule-loader-pill" />
                    <div className="schedule-loader-pill" />
                    <div className="schedule-loader-pill" />
                    <div className="schedule-loader-pill" />
                    <div className="schedule-loader-pill" />
                    <div className="schedule-loader-pill" />
                  </div>
                </div>
              ) : (
                <DayTimeSlots
                  date={selectedDate}
                  slots={slotsByDate[selectedDate] || []}
                  selectedJobId={selectedJobId}
                  onSelectSlot={onOpenAppointment}
                  isDirect={isDirect}
                  byDaysSchedule={byDaysSchedule}
                  patients={patients}
                  jobs={jobs}
                />
              )}
            </div>
          </div>
          <div className="schedule-availability-note">
            Доступність часу для запису актуальна станом на момент перегляду та може змінюватися.
          </div>
        </>
      )}

      {!selectedDate && (
        <div className="calendar-empty">
          <div className="schedule-body-no-result">
            <div className="schedule-body-no-result-content">
              <i className="fas fa-calendar-times" />
              Оберіть дату для перегляду доступних слотів
            </div>
          </div>
        </div>
      )}

      {isDirect && availableDays[selectedDate]?.length > 0 && (
        <div className="schedule-info">
          <div className="schedule-info__icon">
            <span className="schedule-time-list__item-icon">
              <ScheduleInfoSvg />
            </span>
          </div>
          Запис можливий за направленням ЕСОЗ
        </div>
      )}
      {currentDoctorSchedule.schedule.some(slot => slot.time_slot_type_system_name === 'defencer_benefit') && (
        <div className="schedule-info">
          <div className="schedule-info__icon">
            <span
              className="schedule-time-list__item-icon schedule-time-list__item-icon--defender"
              role="img"
              aria-label="Запис за посвідченням для захисників України"
            />
          </div>
          Запис за посвідченням: ветерани війни, Захисники та Захисниці України, члени сімей загиблих.
        </div>
      )}
      {currentDoctorSchedule.schedule.some(slot => slot.time_slot_type_system_name === 'paid') && (
        <div className="schedule-info">
          <div className="schedule-info__icon">
            <span className="schedule-time-list__item-icon">
              $
            </span>
          </div>
          Платні послуги
        </div>
      )}
    </div>
  );
}
